/**
 * Herramientas de Gavi inteligente (Vadik, 31-oct-2026). Las define el
 * servidor (app-rumbo-gavi.ts) y las ejecuta este móvil sobre su presupuesto
 * descifrado. TODAS son de solo lectura: consultas y la hoja del presupuesto,
 * nunca nada que escriba. Devuelven JSON corto (como mucho MAX_RESULTADO
 * caracteres) con importes en euros; las cuentas las hace el código
 * (calculos.ts), no Claude.
 */
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { leerDatosPrestamo } from '@actual-app/core/shared/prestamos';
import { q } from '@actual-app/core/shared/query';

import { leerTipos, tipoDeCuenta } from '#components/mobile/accounts/cuentas';
import { sumarMeses } from '#components/mobile/deudas/amortizacion';
import { aqlQuery } from '#queries/aqlQuery';

import {
  calcular,
  euros,
  finDeDeuda,
  patrimonio,
  ritmoPatrimonio,
} from './calculos';

export const MAX_RESULTADO = 12_000;
const LARGO_TEXTO = 40;

export type Pantalla =
  | 'plan'
  | 'asignar'
  | 'movimientos'
  | 'por_revisar'
  | 'cuentas'
  | 'informes'
  | 'ahorros';

export const RUTAS_PANTALLA: Record<Pantalla, string> = {
  plan: '/budget',
  asignar: '/asignar',
  movimientos: '/gastos',
  por_revisar: '/revisar',
  cuentas: '/accounts',
  informes: '/reports',
  ahorros: '/ahorros',
};

export type ResultadoHerramienta = {
  contenido: string;
  esError?: boolean;
  /** Botón «Llévame» que pide `proponer_pantalla`. */
  pantalla?: Pantalla;
};

/** Texto libre (comercio, categoría) recortado y sin cifras largas. */
export function limpiarTexto(texto: unknown, largo = LARGO_TEXTO): string {
  const t = String(texto ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\d[\d\s-]{7,}\d/g, m => m.replace(/\d/g, '•'));
  return t.length > largo ? `${t.slice(0, largo - 1)}…` : t;
}

function esMes(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}$/.test(v);
}

function esFecha(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function entero(v: unknown, min: number, max: number, defecto: number) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : defecto;
}

function mesDe(v: unknown) {
  return esMes(v) ? v : monthUtils.currentMonth();
}

function json(valor: unknown): string {
  const texto = JSON.stringify(valor);
  return texto.length > MAX_RESULTADO
    ? `${texto.slice(0, MAX_RESULTADO)}… (recortado)`
    : texto;
}

type CategoriaMes = {
  id: string;
  name: string;
  hidden?: boolean;
  budgeted?: number;
  spent?: number;
  balance?: number;
  received?: number;
};

type GrupoMes = {
  id: string;
  name: string;
  hidden?: boolean;
  is_income?: boolean;
  budgeted?: number;
  spent?: number;
  balance?: number;
  received?: number;
  categories?: CategoriaMes[];
};

type Mes = {
  toBudget: number;
  totalBudgeted: number;
  totalIncome: number;
  totalSpent: number;
  totalBalance: number;
  categoryGroups: GrupoMes[];
};

async function leerMes(mes: string): Promise<Mes> {
  return (await send('api/budget-month', { month: mes })) as unknown as Mes;
}

function gruposDeGasto(m: Mes) {
  return m.categoryGroups.filter(g => !g.is_income && !g.hidden);
}

/** Las consultas se arman aparte para poder probarlas con el compilador real. */
export function consultaCuentas() {
  return q('accounts')
    .filter({ closed: false })
    .select(['id', 'name', 'offbudget']);
}

export function consultaSaldosPorCuenta() {
  return q('transactions')
    .filter({ 'account.closed': false })
    .groupBy('account')
    .select([{ cuenta: 'account' }, { saldo: { $sum: '$amount' } }]);
}

/** Como la pestaña Cuentas: todas las notas (las de cuenta, `account-<id>`). */
export function consultaNotasCuentas() {
  return q('notes').select(['id', 'note']);
}

type CuentaLeida = {
  id: string;
  name: string;
  offbudget: boolean;
  /** Céntimos. */
  saldo: number;
};

async function leerCuentas(): Promise<CuentaLeida[]> {
  const { data: cuentas } = (await aqlQuery(consultaCuentas())) as {
    data: Omit<CuentaLeida, 'saldo'>[];
  };
  const { data: sumas } = (await aqlQuery(consultaSaldosPorCuenta())) as {
    data: { cuenta: string; saldo: number }[];
  };
  const saldo = new Map(sumas.map(s => [s.cuenta, s.saldo]));
  return cuentas.map(c => ({ ...c, saldo: saldo.get(c.id) ?? 0 }));
}

async function saldosCuentas() {
  return (await leerCuentas()).map(c => ({
    nombre: limpiarTexto(c.name),
    saldo: euros(c.saldo),
    enPresupuesto: !c.offbudget,
    deuda: c.saldo < 0,
  }));
}

/** Cuentas con su tipo, como en la pestaña Cuentas, y sus datos de préstamo. */
async function cuentasConTipo() {
  const cuentas = await leerCuentas();
  const { data: notas } = (await aqlQuery(consultaNotasCuentas())) as {
    data: { id: string; note: string | null }[];
  };
  const notaDe = new Map(notas.map(n => [n.id, n.note]));
  const { 'cuentas-tipos': tiposRaw } = await send('preferences/get');
  const tipos = leerTipos(tiposRaw);
  return cuentas.map(c => {
    const datos = leerDatosPrestamo(notaDe.get(`account-${c.id}`));
    return {
      ...c,
      datos,
      tipo: tipoDeCuenta(
        { id: c.id, name: c.name, offbudget: c.offbudget ? 1 : 0 },
        datos != null,
        tipos,
      ),
    };
  });
}

async function patrimonioNeto() {
  return patrimonio(
    (await cuentasConTipo()).map(c => ({
      nombre: limpiarTexto(c.name),
      tipo: c.tipo,
      saldo: c.saldo,
    })),
  );
}

/** Lo pagado (entradas positivas) en las cuentas de deuda desde una fecha, por mes. */
export function consultaPagosDeudas(cuentas: string[], desde: string) {
  return q('transactions')
    .filter({
      account: { $oneof: cuentas },
      amount: { $gt: 0 },
      starting_balance_flag: false,
      date: { $gte: desde },
    })
    .groupBy(['account', { $month: '$date' }])
    .select([
      { cuenta: 'account' },
      { mes: { $month: '$date' } },
      { pagado: { $sum: '$amount' } },
    ]);
}

function numeroONull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Meses completos de pagos que se miran si la deuda no tiene cuota. */
const MESES_PAGOS = 3;

async function finDeuda(entrada: Record<string, unknown>) {
  const buscada = textoDeBusqueda(entrada.cuenta)?.toLowerCase();
  const extra = Number(entrada.extra_mensual);
  const extraCentimos =
    Number.isFinite(extra) && extra > 0 ? Math.round(extra * 100) : 0;
  const deudas = (await cuentasConTipo()).filter(
    c =>
      (c.tipo === 'prestamo' || c.saldo < 0) &&
      (!buscada || c.name.toLowerCase().includes(buscada)),
  );
  if (deudas.length === 0) {
    return {
      deudas: [],
      nota: buscada ? 'No hay ninguna deuda con ese nombre.' : 'No hay deudas.',
    };
  }
  const mesActual = monthUtils.currentMonth();
  const meses = Array.from({ length: MESES_PAGOS }, (_, i) =>
    sumarMeses(mesActual, i - MESES_PAGOS),
  );
  const { data: pagos } = (await aqlQuery(
    consultaPagosDeudas(
      deudas.map(d => d.id),
      `${meses[0]}-01`,
    ),
  )) as { data: { cuenta: string; mes: string; pagado: number }[] };
  const pagado = (cuenta: string, mes: string) =>
    pagos.find(p => p.cuenta === cuenta && p.mes === mes)?.pagado ?? 0;
  const lista = deudas.map(c => {
    // La nota la escribe la persona: solo números de verdad salen hacia Claude.
    const cuota = numeroONull(c.datos?.cuota_minima);
    return finDeDeuda({
      nombre: limpiarTexto(c.name),
      saldo: Math.max(0, -c.saldo),
      interesAnual: numeroONull(c.datos?.interes_anual),
      cuota: cuota == null ? null : Math.round(cuota * 100),
      pagosRecientes: meses.map(m => pagado(c.id, m)),
      // Como la ficha de la deuda: si ya se pagó este mes, toca el siguiente.
      mesProximoPago:
        pagado(c.id, mesActual) > 0 ? sumarMeses(mesActual, 1) : mesActual,
      extra: extraCentimos,
    });
  });
  const pendientes = lista.filter(d => d.estado !== 'pagada');
  const fines = pendientes.map(d => ('ultimoPago' in d ? d.ultimoPago : null));
  return {
    deudas: lista,
    deudasPendientes: pendientes.length,
    // El mes en que se acaba la última; null si no queda ninguna o si alguna
    // no se puede calcular.
    todasPagadasEn:
      pendientes.length > 0 && fines.every(f => f != null)
        ? fines.sort().at(-1)
        : null,
  };
}

export function consultaCambioMensual(desde: string, hasta: string) {
  return q('transactions')
    .filter({
      'account.closed': false,
      // El saldo inicial de una cuenta nueva no es un cambio del mes.
      starting_balance_flag: false,
      $and: [
        { date: { $transform: '$month', $gte: desde } },
        { date: { $transform: '$month', $lte: hasta } },
      ],
    })
    .groupBy({ $month: '$date' })
    .select([{ mes: { $month: '$date' } }, { cambio: { $sum: '$amount' } }]);
}

async function ritmo(entrada: Record<string, unknown>) {
  const n = entero(entrada.meses, 3, 12, 6);
  const mesActual = monthUtils.currentMonth();
  // Solo meses completos: el actual va a medias.
  const meses = Array.from({ length: n }, (_, i) =>
    sumarMeses(mesActual, i - n),
  );
  const { data } = (await aqlQuery(
    consultaCambioMensual(meses[0], meses[n - 1]),
  )) as { data: { mes: string; cambio: number }[] };
  const neto = (await leerCuentas()).reduce((s, c) => s + c.saldo, 0);
  return ritmoPatrimonio(data, meses, neto, mesActual);
}

export function consultaSinCategoria(mes: string) {
  return q('transactions')
    .filter({
      date: { $transform: '$month', $eq: mes },
      category: null,
      'account.offbudget': false,
      'payee.transfer_acct': null,
      is_parent: false,
    })
    .calculate({ $count: '$id' });
}

async function resumen(entrada: Record<string, unknown>) {
  const mes = mesDe(entrada.mes);
  const m = await leerMes(mes);
  const cuentas = await saldosCuentas();
  const { data: sinCategoria } = (await aqlQuery(
    consultaSinCategoria(mes),
  )) as { data: number };
  return {
    mes,
    listoParaAsignar: euros(m.toBudget),
    asignado: euros(m.totalBudgeted),
    gastado: euros(-m.totalSpent),
    ingresado: euros(m.totalIncome),
    queda: euros(m.totalBalance),
    grupos: gruposDeGasto(m).map(g => ({
      grupo: limpiarTexto(g.name),
      asignado: euros(g.budgeted),
      gastado: euros(-(g.spent ?? 0)),
      queda: euros(g.balance),
    })),
    saldoCuentasPresupuesto: euros(
      cuentas
        .filter(c => c.enPresupuesto)
        .reduce((s, c) => s + Math.round(c.saldo * 100), 0),
    ),
    movimientosSinCategoria: sinCategoria,
  };
}

async function mesPresupuesto(entrada: Record<string, unknown>) {
  const mes = mesDe(entrada.mes);
  const m = await leerMes(mes);
  return {
    mes,
    categorias: gruposDeGasto(m).flatMap(g =>
      (g.categories ?? [])
        .filter(c => !c.hidden)
        .map(c => ({
          grupo: limpiarTexto(g.name),
          categoria: limpiarTexto(c.name),
          asignado: euros(c.budgeted),
          gastado: euros(-(c.spent ?? 0)),
          queda: euros(c.balance),
        })),
    ),
  };
}

type FilaAgrupada = {
  mes: string;
  categoria: string | null;
  grupo: string | null;
  comercio: string | null;
  importe: number;
};

export function consultaGastoAgrupado(
  desde: string,
  hasta: string,
  filtroCategoria?: string,
) {
  let consulta = q('transactions').filter({
    $and: [
      { date: { $transform: '$month', $gte: desde } },
      { date: { $transform: '$month', $lte: hasta } },
    ],
    'account.offbudget': false,
    $or: [
      { 'payee.transfer_acct': null },
      { 'payee.transfer_acct.offbudget': true },
    ],
  });
  if (filtroCategoria !== undefined) {
    consulta = consulta.filter({
      'category.name': { $like: `%${filtroCategoria}%` },
    });
  }
  return consulta
    .groupBy([{ $month: '$date' }, { $id: '$category' }, { $id: '$payee' }])
    .select([
      { mes: { $month: '$date' } },
      { categoria: 'category.name' },
      { grupo: 'category.group.name' },
      { comercio: 'payee.name' },
      { importe: { $sum: '$amount' } },
    ]);
}

async function gastoAgrupado(entrada: Record<string, unknown>) {
  if (!esMes(entrada.desde) || !esMes(entrada.hasta)) {
    throw new Error('desde y hasta tienen que ser meses AAAA-MM');
  }
  const por = String(entrada.por);
  if (!['categoria', 'grupo', 'comercio', 'mes'].includes(por)) {
    throw new Error('por: categoria, grupo, comercio o mes');
  }
  const top = entero(entrada.top, 1, 20, 10);
  const filtroCategoria =
    typeof entrada.filtro_categoria === 'string'
      ? entrada.filtro_categoria
      : undefined;
  const { data } = (await aqlQuery(
    consultaGastoAgrupado(entrada.desde, entrada.hasta, filtroCategoria),
  )) as { data: FilaAgrupada[] };
  return agrupar(data, por as 'categoria' | 'grupo' | 'comercio' | 'mes', top);
}

/** Suma salidas y entradas por la clave pedida y deja las `top` mayores. Pura. */
export function agrupar(
  filas: readonly FilaAgrupada[],
  por: 'categoria' | 'grupo' | 'comercio' | 'mes',
  top: number,
) {
  const totales = new Map<string, { salidas: number; entradas: number }>();
  for (const f of filas) {
    const clave =
      (por === 'mes' ? f.mes : f[por]) ??
      (por === 'comercio' ? '(sin comercio)' : '(sin categoría)');
    const t = totales.get(clave) ?? { salidas: 0, entradas: 0 };
    if (f.importe < 0) {
      t.salidas -= f.importe;
    } else {
      t.entradas += f.importe;
    }
    totales.set(clave, t);
  }
  const lista = [...totales.entries()].map(([clave, t]) => ({
    [por]: por === 'mes' ? clave : limpiarTexto(clave),
    salidas: euros(t.salidas),
    entradas: euros(t.entradas),
  }));
  if (por === 'mes') {
    lista.sort((a, b) => String(a.mes).localeCompare(String(b.mes)));
  } else {
    lista.sort((a, b) => b.salidas - a.salidas || b.entradas - a.entradas);
  }
  const sumaSalidas = [...totales.values()].reduce((s, t) => s + t.salidas, 0);
  const sumaEntradas = [...totales.values()].reduce(
    (s, t) => s + t.entradas,
    0,
  );
  return {
    filas: lista.slice(0, top),
    filasOcultas: Math.max(0, lista.length - top),
    totalSalidas: euros(sumaSalidas),
    totalEntradas: euros(sumaEntradas),
  };
}

type FilaMovimiento = {
  date: string;
  amount: number;
  comercio: string | null;
  categoria: string | null;
  cuenta: string | null;
};

export function consultaMovimientos({
  desde,
  hasta,
  limite,
  texto,
  categoria,
  importeMinimo = 0,
}: {
  desde: string;
  hasta: string;
  limite: number;
  texto?: string;
  categoria?: string;
  importeMinimo?: number;
}) {
  const filtros: Record<string, unknown>[] = [
    { date: { $gte: desde } },
    { date: { $lte: hasta } },
  ];
  if (texto) {
    filtros.push({ 'payee.name': { $like: `%${texto}%` } });
  }
  if (categoria) {
    filtros.push({ 'category.name': { $like: `%${categoria}%` } });
  }
  if (Number.isFinite(importeMinimo) && importeMinimo > 0) {
    const c = Math.round(importeMinimo * 100);
    filtros.push({ $or: [{ amount: { $lte: -c } }, { amount: { $gte: c } }] });
  }
  return q('transactions')
    .filter({ $and: filtros })
    .select([
      'date',
      'amount',
      { comercio: 'payee.name' },
      { categoria: 'category.name' },
      { cuenta: 'account.name' },
    ])
    .orderBy({ date: 'desc' })
    .limit(limite + 1);
}

/** Texto de búsqueda sin espacios sobrantes y de 60 caracteres como mucho. */
function textoDeBusqueda(v: unknown) {
  return typeof v === 'string' ? v.trim().slice(0, 60) : undefined;
}

async function buscarMovimientos(entrada: Record<string, unknown>) {
  if (!esFecha(entrada.desde) || !esFecha(entrada.hasta)) {
    throw new Error('desde y hasta tienen que ser fechas AAAA-MM-DD');
  }
  const limite = entero(entrada.limite, 1, 30, 15);
  const { data } = (await aqlQuery(
    consultaMovimientos({
      desde: entrada.desde,
      hasta: entrada.hasta,
      limite,
      texto: textoDeBusqueda(entrada.texto),
      categoria: textoDeBusqueda(entrada.categoria),
      importeMinimo: Number(entrada.importe_minimo),
    }),
  )) as { data: FilaMovimiento[] };
  return {
    movimientos: data.slice(0, limite).map(m => ({
      fecha: m.date,
      importe: euros(m.amount),
      comercio: limpiarTexto(m.comercio),
      categoria: m.categoria ? limpiarTexto(m.categoria) : null,
      cuenta: limpiarTexto(m.cuenta),
    })),
    hayMas: data.length > limite,
  };
}

async function quePasaSi(entrada: Record<string, unknown>) {
  const importe = Number(entrada.importe);
  if (!Number.isFinite(importe) || importe <= 0) {
    throw new Error('importe tiene que ser un número positivo');
  }
  const mes = mesDe(entrada.mes);
  const m = await leerMes(mes);
  const categorias = gruposDeGasto(m).flatMap(g =>
    (g.categories ?? []).filter(c => !c.hidden),
  );
  const buscada =
    typeof entrada.categoria === 'string'
      ? entrada.categoria.trim().toLowerCase()
      : '';
  const cat = buscada
    ? (categorias.find(c => c.name.toLowerCase() === buscada) ??
      categorias.find(c => c.name.toLowerCase().includes(buscada)))
    : undefined;
  return calcularQuePasaSi({
    mes,
    importe: Math.round(importe * 100),
    listo: m.toBudget,
    categoria: cat
      ? { nombre: cat.name, queda: cat.balance ?? 0 }
      : buscada
        ? null
        : undefined,
    otras: categorias
      .filter(c => c !== cat && (c.balance ?? 0) > 0)
      .map(c => ({ nombre: c.name, queda: c.balance ?? 0 })),
  });
}

/** Cuentas de «¿me da para…?» en céntimos; devuelve euros. Pura. */
export function calcularQuePasaSi({
  mes,
  importe,
  listo,
  categoria,
  otras,
}: {
  mes: string;
  importe: number;
  listo: number;
  /** undefined: sin categoría; null: no se encontró. */
  categoria: { nombre: string; queda: number } | null | undefined;
  otras: { nombre: string; queda: number }[];
}) {
  const quedaCategoria = categoria?.queda ?? 0;
  const falta = Math.max(0, importe - Math.max(0, quedaCategoria));
  const deListo = Math.min(falta, Math.max(0, listo));
  const faltaTrasListo = falta - deListo;
  const fuentes = [...otras].sort((a, b) => b.queda - a.queda).slice(0, 5);
  const enFuentes = fuentes.reduce((s, f) => s + f.queda, 0);
  return {
    mes,
    importe: euros(importe),
    categoria:
      categoria === null
        ? 'no encontrada'
        : categoria
          ? limpiarTexto(categoria.nombre)
          : null,
    quedaEnCategoria: categoria ? euros(quedaCategoria) : null,
    quedariaEnCategoria: categoria ? euros(quedaCategoria - importe) : null,
    cubiertoPorLaCategoria: falta === 0,
    faltaTrasLaCategoria: euros(falta),
    listoParaAsignar: euros(listo),
    sePuedeCubrirConListoParaAsignar: faltaTrasListo === 0,
    faltaTrasListoParaAsignar: euros(faltaTrasListo),
    categoriasConSobra: fuentes.map(f => ({
      categoria: limpiarTexto(f.nombre),
      queda: euros(f.queda),
    })),
    sobraTotalEnEsasCategorias: euros(enFuentes),
    alcanzaMoviendoDeEsas: faltaTrasListo <= enFuentes,
  };
}

/** Ejecuta una herramienta que ha pedido Claude. Nunca lanza. */
export async function ejecutarHerramienta(
  nombre: string,
  entrada: Record<string, unknown>,
): Promise<ResultadoHerramienta> {
  try {
    switch (nombre) {
      case 'resumen':
        return { contenido: json(await resumen(entrada)) };
      case 'mes_presupuesto':
        return { contenido: json(await mesPresupuesto(entrada)) };
      case 'gasto_agrupado':
        return { contenido: json(await gastoAgrupado(entrada)) };
      case 'buscar_movimientos':
        return { contenido: json(await buscarMovimientos(entrada)) };
      case 'cuentas':
        return { contenido: json({ cuentas: await saldosCuentas() }) };
      case 'que_pasa_si':
        return { contenido: json(await quePasaSi(entrada)) };
      case 'calcular':
        return {
          contenido: json({
            expresion: entrada.expresion,
            resultado: calcular(entrada.expresion),
          }),
        };
      case 'patrimonio_neto':
        return { contenido: json(await patrimonioNeto()) };
      case 'fin_deuda':
        return { contenido: json(await finDeuda(entrada)) };
      case 'ritmo_patrimonio':
        return { contenido: json(await ritmo(entrada)) };
      case 'proponer_pantalla': {
        const pantalla = entrada.pantalla as Pantalla;
        if (!(pantalla in RUTAS_PANTALLA)) {
          return { contenido: 'Pantalla desconocida.', esError: true };
        }
        return { contenido: 'Botón puesto.', pantalla };
      }
      default:
        return {
          contenido: `Herramienta desconocida: ${nombre}`,
          esError: true,
        };
    }
  } catch (e) {
    return {
      contenido: `Error: ${String((e as Error)?.message ?? e).slice(0, 200)}`,
      esError: true,
    };
  }
}
